import { Box, Portal, Tooltip, TooltipProps } from "@mui/material";
import { ComponentProps, ReactElement, useId } from "react";

type DescribedTooltipProps = Omit<
  TooltipProps,
  "title" | "children" | "describeChild"
> & {
  title: string;
  renderTrigger: (ariaDescribedById: string) => ReactElement<unknown>;
};

/**
 * A tooltip whose text stays the trigger's accessible description while
 * closed. MUI only sets it on the child's root while open (a span for a
 * Checkbox), and icons are neither focusable nor announced.
 */
export const DescribedTooltip = ({
  title,
  renderTrigger,
  ...tooltipProps
}: DescribedTooltipProps) => {
  const descriptionId = useId();
  return (
    <>
      {/* A node title keeps MUI from adding a native title to the trigger. */}
      <Tooltip
        {...tooltipProps}
        title={<Box sx={{ whiteSpace: "pre-line" }}>{title}</Box>}
        describeChild
      >
        {renderTrigger(descriptionId)}
      </Tooltip>
      <HiddenDescription id={descriptionId}>{title}</HiddenDescription>
    </>
  );
};

/** Portaled so the text doesn't add to a surrounding label's text. */
export const HiddenDescription = ({
  id,
  children,
}: {
  id: string;
  children: string;
}) => (
  <Portal>
    <span id={id} hidden>
      {children}
    </span>
  </Portal>
);

export const TooltipTrigger = ({
  label,
  ...props
}: Omit<ComponentProps<"span">, "role" | "tabIndex" | "aria-label"> & {
  label: string;
}) => (
  <Box
    component="span"
    {...props}
    role="img"
    aria-label={label}
    tabIndex={0}
    sx={{ display: "inline-flex", verticalAlign: "middle" }}
  />
);
